/**
 * Filtering for the map's advertisement layers: property type crossed with
 * deal type.
 *
 * Two data sources have to be combined, because neither field alone is enough:
 *
 * - Property type ("فيلا", "شقة", …) lives only in the API, as
 *   NhcIntegration.PropertyType. The map layers have no such field.
 * - Deal type lives only on the map, encoded in BaseLandType. The ads table
 *   does not persist AdvertisementType at all.
 *
 * So a row's filter is "these ad object ids" AND "these deal codes". Filtering
 * runs on BaseLandType rather than the Arabic Parcel_Status label because the
 * service does not match Arabic literals in a where clause — such a query
 * returns zero rows and no error.
 */

export interface MapAd {
  /** Matches the Parcel_Cod attribute on the map's parcel layers. */
  objectId: number;
  propertyType: string;
}

export type DealTypeId = "sale" | "rentAndInvestment";

export interface DealType {
  id: DealTypeId;
  /** Default label; land uses dealLabelFor to say استثمار instead of إيجار. */
  label: string;
  /** BaseLandType values that mean this deal. */
  codes: string[];
}

export const DEAL_TYPES: DealType[] = [
  { id: "sale", label: "بيع", codes: ["1", "4"] },
  // The service stores rent and investment as one combined value ("ايجار
  // واستثمار", code 3), so this is a single option labelled with both words,
  // not two separate choices.
  { id: "rentAndInvestment", label: "إيجار واستثمار", codes: ["3"] },
];

/**
 * Codes present on the layers that carry no deal meaning (mixed-use buildings).
 * They are admitted under every deal choice: hiding them would make parcels
 * disappear with no way for the user to get them back.
 */
export const UNKNOWN_DEAL_CODES = ["5", "-50"];

/** Base filter already applied to the parcels layer (non-featured, not deleted). */
export const OFFERS_BASE_WHERE =
  "Feature_Ad IS NULL AND (IsDeleted IS NULL OR IsDeleted <> 'true')";

/** Base filter already applied to the heatmap layer (not deleted). */
export const HEATMAP_BASE_WHERE = "(IsDeleted IS NULL OR IsDeleted <> 'true')";

const normalize = (value: string): string => (value || "").trim();

/**
 * Label for a deal option. The label is the same for every property type: the
 * rent/investment bucket is one combined value in the data, so it reads
 * "إيجار واستثمار" everywhere rather than being split per type.
 */
export const dealLabelFor = (propertyType: string, deal: DealTypeId): string =>
  DEAL_TYPES.find((d) => d.id === deal)!.label;

/**
 * Distinct property types present in the ads, in the order the dropdown shows.
 *
 * The order comes from the API, which ranks the types by how many ads carry
 * each one. Nothing is ranked here: a list kept in the client drifts from the
 * data the moment a new property type appears in an advertisement.
 *
 * `order` is filtered down to the types actually present, and any present type
 * the API did not mention is appended rather than hidden.
 */
export const listPropertyTypes = (ads: MapAd[], order?: string[]): string[] => {
  const present: string[] = [];

  ads.forEach((ad) => {
    const type = normalize(ad.propertyType);
    if (type && present.indexOf(type) === -1) {
      present.push(type);
    }
  });

  if (!order || order.length === 0) {
    // Falls back to the ads' own sequence, which the API already groups by type.
    return present;
  }

  const ranked = order
    .map(normalize)
    .filter((type, index, all) => type && all.indexOf(type) === index && present.indexOf(type) !== -1);

  const unranked = present.filter((type) => ranked.indexOf(type) === -1);

  return ranked.concat(unranked);
};

/**
 * What a property type is filtered to: its chosen deal, or null when the user
 * unchecked it.
 *
 * null and "absent" mean different things, and conflating them is a bug: an
 * absent type has never been seen before and starts checked, while a null one
 * was deliberately switched off and must stay off.
 */
export type FilterSelection = Record<string, DealTypeId | null>;

/** Every present type checked and set to sale — the state the map opens with. */
export const defaultSelection = (ads: MapAd[]): FilterSelection =>
  listPropertyTypes(ads).reduce<FilterSelection>((acc, type) => {
    acc[type] = "sale";
    return acc;
  }, {});

/**
 * Merge a stored selection with the types currently present.
 *
 * Types the stored selection has never seen start checked; types it recorded as
 * null stay unchecked. Returning "sale" for a null entry would make unchecking
 * impossible, since the row would come straight back on the next render.
 */
export const reconcileSelection = (
  ads: MapAd[],
  stored: FilterSelection | null
): FilterSelection => {
  if (stored === null) {
    return defaultSelection(ads);
  }

  return listPropertyTypes(ads).reduce<FilterSelection>((acc, type) => {
    acc[type] = Object.prototype.hasOwnProperty.call(stored, type) ? stored[type] : "sale";
    return acc;
  }, {});
};

/** Property types currently switched on. */
export const checkedTypes = (selection: FilterSelection): string[] =>
  Object.keys(selection).filter((type) => selection[type] != null);

/**
 * Compose a definitionExpression limiting a layer to the checked rows.
 *
 * Returns null when nothing resolves to a filterable row — the caller hides the
 * layer, since `IN ()` is not valid SQL.
 */
export const buildOffersWhere = (
  baseWhere: string,
  ads: MapAd[],
  selection: FilterSelection
): string | null => {
  const presentTypes = listPropertyTypes(ads);

  // Grouped by deal rather than by row: types sharing a deal share one clause,
  // which keeps the expression from growing with the number of selected rows.
  const clauses = DEAL_TYPES.map((deal) => {
    const types = presentTypes.filter((type) => selection[type] === deal.id);

    if (types.length === 0) {
      return null;
    }

    const codes = deal.codes
      .concat(UNKNOWN_DEAL_CODES)
      .map((code) => `'${code}'`)
      .join(",");

    // When this deal covers every type present, the id list says nothing the
    // codes do not. Dropping it keeps the query short enough for the service to
    // accept — with every type listed the ids alone overflow the URL limit.
    if (types.length === presentTypes.length) {
      return `BaseLandType IN (${codes})`;
    }

    const objectIds = ads
      .filter((ad) => types.indexOf(normalize(ad.propertyType)) !== -1)
      .map((ad) => ad.objectId);

    // Would produce "IN ()", which the service rejects.
    if (objectIds.length === 0) {
      return null;
    }

    return `(Parcel_Cod IN (${objectIds.join(",")}) AND BaseLandType IN (${codes}))`;
  }).filter((clause): clause is string => clause !== null);

  if (clauses.length === 0) {
    return null;
  }

  const joined = clauses.join(" OR ");

  if (baseWhere) {
    return `${baseWhere} AND (${joined})`;
  }

  return clauses.length === 1 ? joined : `(${joined})`;
};
